"use client";

import {
  deleteRacArisRule,
  editRacArisRule,
  addRacArisRule,
} from "@/app/lib/actions/rac-aris-rules";

import { ItemContent, ItemTitle } from "@/components/ui/item";

import {
  ItemsListCard,
  ItemsListCardSkeleton,
} from "@/components/backoffice/items-list-card";

import {
  useRacArisRulesQuery,
  invalidateRacArisRulesQuery,
} from "@/lib/queries/rac-aris-rules";

import { useQueryClient } from "@tanstack/react-query";

import * as z from "zod";

import {
  addRacArisRuleSchema,
  editRacArisRuleSchema,
} from "@/lib/schemas/rac-aris-rules";

import { Input } from "@/components/ui/input";

import { Switch } from "@/components/ui/switch";

import {
  Field,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "@/components/ui/field";

import { toast } from "sonner";

type RacArisRule = {
  id: number;

  kvps: string;

  renterIdentifier: string;

  financialOrganizationIdentifier: string;

  renterOperatorIdentifier: string;

  isActive: boolean;
};

function RacArisRulesItemsListCard() {
  const queryClient = useQueryClient();

  const racArisRulesQuery = useRacArisRulesQuery({
    sort: {
      kvps: "asc",
    },
  });

  const handleCreate = async (
    racRule: z.infer<typeof addRacArisRuleSchema>,
  ) => {
    const response = await addRacArisRule(racRule);

    if (!response.success) {
      toast.error(response.message);
    } else {
      toast.success("Règle RAC ARIS créée.");

      invalidateRacArisRulesQuery(queryClient);
    }
  };

  const handleUpdate = async (
    racRule: z.infer<typeof editRacArisRuleSchema>,
  ) => {
    const response = await editRacArisRule(racRule);

    if (!response.success) {
      toast.error(response.message);
    } else {
      toast.success("Règle RAC ARIS modifiée.");

      invalidateRacArisRulesQuery(queryClient);
    }
  };

  const handleDelete = async (id: { id: number }) => {
    const response = await deleteRacArisRule(id);

    if (!response.success) {
      toast.error(response.message);
    } else {
      toast.success("Règle RAC ARIS supprimée.");

      invalidateRacArisRulesQuery(queryClient);
    }
  };

  if (racArisRulesQuery.isPending) {
    return <ItemsListCardSkeleton />;
  }

  const racArisRules = racArisRulesQuery.data?.racArisRules ?? [];

  return (
    <ItemsListCard<RacArisRule>
      title="Paramétrage RAC ARIS"
      items={racArisRules}
      createSchema={addRacArisRuleSchema}
      editSchema={editRacArisRuleSchema}
      defaultCreateValues={{
        kvps: "",
        renterIdentifier: "",
        financialOrganizationIdentifier: "",
        renterOperatorIdentifier: "",
        isActive: true,
      }}
      onCreate={handleCreate}
      onUpdate={handleUpdate}
      onDelete={handleDelete}
      renderItem={(item) => (
        <>
          <ItemContent>
            <ItemTitle>{item.kvps}</ItemTitle>

            <div className="flex flex-wrap items-center gap-3 text-xs text-slate-700">
              <div className="flex items-center gap-1">
                <span className="text-slate-500">Loueur :</span>
                <span className="font-mono">{item.renterIdentifier}</span>
              </div>

              <span className="text-slate-300">|</span>

              <div className="flex items-center gap-1">
                <span className="text-slate-500">Organisme :</span>
                <span className="font-mono">
                  {item.financialOrganizationIdentifier}
                </span>
              </div>

              <span className="text-slate-300">|</span>

              <div className="flex items-center gap-1">
                <span className="text-slate-500">Exploitant :</span>
                <span className="font-mono">
                  {item.renterOperatorIdentifier}
                </span>
              </div>
            </div>
          </ItemContent>
        </>
      )}
      renderFormFields={(form) => (
        <FieldGroup>
          {/* KVPS */}
          <form.Field name="kvps">
            {(field) => {
              const isInvalid =
                field.state.meta.isTouched && !field.state.meta.isValid;

              return (
                <Field data-invalid={isInvalid}>
                  <FieldLabel htmlFor={field.name}>KVPS</FieldLabel>

                  <Input
                    id={field.name}
                    name={field.name}
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                    aria-invalid={isInvalid}
                    maxLength={9}
                  />

                  {isInvalid && <FieldError errors={field.state.meta.errors} />}
                </Field>
              );
            }}
          </form.Field>

          <form.Field name="renterIdentifier">
            {(field) => {
              const isInvalid =
                field.state.meta.isTouched && !field.state.meta.isValid;

              return (
                <Field data-invalid={isInvalid}>
                  <FieldLabel htmlFor={field.name}>
                    Identifiant loueur
                  </FieldLabel>

                  <Input
                    id={field.name}
                    name={field.name}
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                    aria-invalid={isInvalid}
                    maxLength={4}
                  />

                  {isInvalid && <FieldError errors={field.state.meta.errors} />}
                </Field>
              );
            }}
          </form.Field>

          <form.Field name="financialOrganizationIdentifier">
            {(field) => {
              const isInvalid =
                field.state.meta.isTouched && !field.state.meta.isValid;

              return (
                <Field data-invalid={isInvalid}>
                  <FieldLabel htmlFor={field.name}>
                    Identification organisme financier
                  </FieldLabel>

                  <Input
                    id={field.name}
                    name={field.name}
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                    aria-invalid={isInvalid}
                    maxLength={6}
                  />

                  {isInvalid && <FieldError errors={field.state.meta.errors} />}
                </Field>
              );
            }}
          </form.Field>

          <form.Field name="renterOperatorIdentifier">
            {(field) => {
              const isInvalid =
                field.state.meta.isTouched && !field.state.meta.isValid;

              return (
                <Field data-invalid={isInvalid}>
                  <FieldLabel htmlFor={field.name}>
                    Identification loueur titulaire / exploitant
                  </FieldLabel>

                  <Input
                    id={field.name}
                    name={field.name}
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                    aria-invalid={isInvalid}
                    maxLength={6}
                  />

                  {isInvalid && <FieldError errors={field.state.meta.errors} />}
                </Field>
              );
            }}
          </form.Field>

          <form.Field name="isActive">
            {(field) => (
              <Field>
                <FieldLabel htmlFor={field.name}>Statut</FieldLabel>

                <Switch
                  id="switch"
                  checked={field.state.value}
                  onCheckedChange={field.handleChange}
                />
              </Field>
            )}
          </form.Field>
        </FieldGroup>
      )}
    />
  );
}

export { RacArisRulesItemsListCard };
